import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import VideoCard from './media/VideoCard';
import { api } from '../lib/api';

// Homepage teaser for the video library — same published-only data as
// /media, trimmed to a handful of entries. Renders an honest empty state
// instead of fabricated or empty-looking cards when nothing is published.
export default function VideoSection() {
  const { t } = useTranslation();
  const [videos, setVideos] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .listVideos()
      .then((data) => {
        if (active) setVideos(data.videos || []);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const featured = (videos || []).slice(0, 3);

  return (
    <Box component="section" sx={{ py: { xs: 7, md: 10 }, borderTop: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          justifyContent="space-between"
          sx={{ mb: 4 }}
          spacing={2}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: 'text.secondary',
                mb: 1
              }}
            >
              {t('home.videoSection.eyebrow')}
            </Typography>
            <Typography
              component="h2"
              sx={{ fontFamily: 'var(--font-serif)', fontSize: { xs: '2rem', md: '2.8rem' }, lineHeight: 1.06, letterSpacing: '-0.02em' }}
            >
              {t('home.videoSection.title')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', mt: 1.5, maxWidth: 640 }}>{t('home.videoSection.intro')}</Typography>
          </Box>
          <Button component={RouterLink} to="/media" variant="text" endIcon={<ArrowForwardRoundedIcon />} sx={{ color: 'text.primary' }}>
            {t('home.videoSection.cta')}
          </Button>
        </Stack>

        {!error && videos && featured.length === 0 ? (
          <Typography sx={{ color: 'text.secondary' }}>{t('home.videoSection.empty')}</Typography>
        ) : null}
        {error ? <Typography sx={{ color: 'text.secondary' }}>{t('home.videoSection.empty')}</Typography> : null}

        {featured.length > 0 ? (
          <Grid container spacing={3}>
            {featured.map((video) => (
              <Grid key={video.id} item xs={12} md={4}>
                <VideoCard video={video} />
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Container>
    </Box>
  );
}
